import parsePhoneNumberFromString from "libphonenumber-js";
import { EyeOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Col, Collapse, Row, Spin, Table, message, Tag, Divider } from "antd";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { NavLink } from "react-router-dom";
import BaseForm from "../components/Form/BaseForm";
import entitlements from "../config/entitlements";
import { ALL_USERS_QUERY } from "../gql/users";
import {
  Activity,
  Attachment,
  Company,
  CompanyWithCustomerCompanyAssociationId,
  Customer,
  FormFieldConfig,
  InteractionSurveyOutcome,
  normalizeInteractionClosureMetadata,
  Product,
  ProductWithSubscriptionId,
  SelectOptionsInterface,
  SubCategory,
  Subscriber,
  SubscriberListItem,
  Subscription,
  TransferRequest,
  User,
} from "../helpers/shared-interfaces";
import {
  generateComplaintTypes,
  generateContactTypes,
  generateDisplayErrorMessage,
  generateFirstSelectOption,
  injectKey,
  request,
  responseHandler,
  getStateColor,
  formatSnakeCase,
} from "../helpers/utilities";
import { interactionCategoryOptions } from "../helpers/constants";
import AttachmentsSection from "./AttachmentsSection";
import EntitledSection from "./EntitledSection";
import ExportData from "./ExportData";
import ModalCreator from "./ModalCreator";
import { DEBOUNCE_TIME } from "../helpers/constants";

interface Props {
  style?: React.CSSProperties;
  forCustomer: boolean;
  forCompany: boolean;
  useBaseColumn: boolean;
  detailLayout?: "table" | "columns";
  showDetailActions?: boolean;
  showHeading: boolean;
  activities: Activity[];
  createInteraction: (
    ccIds: CompanyWithCustomerCompanyAssociationId[],
    customerSubscriptionIds: ProductWithSubscriptionId[],
  ) => Promise<void>;
  hasAttachment?: boolean;
  attachments: Attachment[];
  updateAttachments: (attachment: Attachment) => void;
  removeAttachment: (id: string) => void;
  reference: RefObject<FormInstance>;
  showForm: boolean;
  showBorder: boolean;
  showInteraction: boolean;
  isInteractionLoading: boolean;
  showRequests?: boolean;
  toggleViewInteractionSection: (currentActivityId: string) => void;
  toggleViewRequestsSection: (currentActivityId: string) => void;
  toggleViewRedemptionRequestsSection: (currentActivityId: string) => void;
  toggleInteractionEdit: () => void;
  toggleCreateTicket: () => void;
  toggleCreateInteractionModal: () => void;
  updateInteraction?: () => Promise<void>;
  isEditing?: boolean;
  isTicketFormOpen?: boolean;
  actionLabelMode?: "default" | "closeOnOpen";
  transferRequests: TransferRequest[] | [];
  customer?: Customer;
  company?: Company;
  nextPage?: (page: number) => Promise<void>;
  handlePanelChange?: (key: string | string[]) => void;
  activitiesCount: number;
  disableCreateInteractionButton?: boolean;
  isReadOnly?: boolean;
  createInteractionButtonRef?: React.RefObject<HTMLButtonElement>;
  assignAttachmentsToCompanyInteraction?: (
    attachmentIds: string[],
    companyInteractionId: string,
  ) => void;
  assignAttachmentsToCustomerInteraction?: (
    attachmentIds: string[],
    companyInteractionId: string,
  ) => void;
  currentInteractionId?: string;
}

interface State {
  interactionFieldsToHide: string[];
  complaintFieldsToHide: string[];
  subCategories: SubCategory[];
  allSubCategories: SubCategory[];
  interactionSources: SelectOptionsInterface[];
  preferredCommMode: string;
  commModeFieldsToHide: string[];
  ticketFieldToHide: string[];
  productsWithSubscriptionId: ProductWithSubscriptionId[];
  selectedProduct: ProductWithSubscriptionId[];
  companiesWithCustomerCompanyAssociationId: CompanyWithCustomerCompanyAssociationId[];
  selectedCompany: CompanyWithCustomerCompanyAssociationId[];
  products: Product[];
  companies: Company[];
  isLoading: boolean;
  subscribers: SubscriberListItem[];
  mentions: number[];
  users: User[];
  userQuery: string;
  subCategoryQuery: string;
  allSubCategoryQuery: string;
  isComplaint: boolean;
  ticketSubCategoryDueDate: string;
  sourceContactType: any;
}

const GET_INTERACTION_SOURCES = `
query {
  interactionSources {
    id
    name
  }
}
`;

export const GET_SUBSCRIBERS = `
query {
  subscribers {
    id
    user{
      id
      name
    }
  }
}
`;

export const GET_SUB_CATEGORIES = `
query GetSubCategories($page: ID, $q: String, $departmentId: ID){
  subCategories(page: $page, q: $q, departmentId: $departmentId){
    count
    results {
      id
      longName
    }
    errors {
      property
      description
    }
  }
}
`;

export const CREATE_ATTACHMENT = `
  mutation CreateAttachment($url: String!, $name: String) {
    createAttachment(url: $url, name: $name){
       attachment {
        id
        url
        name
      }
      errors {
        property
        description
      }
    }
  }
`;

export const CALCULATE_DUE_DATE_QUERY = `
  query CalculateDueDate($subCategoryId: ID!, $activityDate: ISO8601DateTime) {
    calculateSubCategoryDueDate(subCategoryId: $subCategoryId, activityDate: $activityDate)
  }
`;

const convertStatusToReadableString: { [x: string]: string } = {
  on_hold: "On Hold",
  closed: "Closed",
  open: "Open",
};

const interactionCategoryLabels: { [x: string]: string } = {
  general_request: "General Request",
  complaint: "Complaint",
};

function formatInteractionCategory(interactionCategory?: string): string {
  if (!interactionCategory) {
    return "N/A";
  }

  return (
    interactionCategoryLabels[interactionCategory] ||
    formatSnakeCase(interactionCategory)
  );
}

const TWO_COLUMN_SPAN = 11;

function applyTwoColumnOffsets(
  fields: FormFieldConfig[],
  fieldsToHide: string[],
): FormFieldConfig[] {
  let inRightColumn = false;

  return fields.map((field) => {
    if (fieldsToHide.includes(field.name) || !field.colConfig) {
      return field;
    }

    if (field.colConfig.span !== TWO_COLUMN_SPAN) {
      inRightColumn = false;
      return { ...field, colConfig: { ...field.colConfig, offset: 0 } };
    }

    const offset = inRightColumn ? 1 : 0;
    inRightColumn = !inRightColumn;
    return { ...field, colConfig: { ...field.colConfig, offset } };
  });
}

function formatSurveyOutcomeValue(value?: string): string {
  return value ? formatSnakeCase(value) : "N/A";
}

function formatSurveyOutcomeDate(recordedAt?: string): string {
  return recordedAt ? moment(recordedAt).format("llll") : "N/A";
}

function renderSurveyOutcome(
  outcome?: InteractionSurveyOutcome,
): JSX.Element | null {
  if (!outcome) {
    return null;
  }

  const surveyFields = [
    {
      label: "Status",
      value: formatSurveyOutcomeValue(outcome.status),
    },
    {
      label: "Channel",
      value:
        outcome.deliveryChannelLabel ||
        formatSurveyOutcomeValue(outcome.deliveryChannel),
    },
    {
      label: "Category",
      value:
        outcome.interactionCategoryLabel ||
        formatInteractionCategory(outcome.interactionCategory),
    },
    {
      label: "Recipient",
      value:
        outcome.sourceContactUsed ||
        outcome.sourceContactInformation ||
        "N/A",
    },
    {
      label: "Subject",
      value: outcome.subject || "N/A",
    },
    {
      label: "Template",
      value: outcome.templateName || "N/A",
    },
    {
      label: "Recorded At",
      value: formatSurveyOutcomeDate(outcome.recordedAt),
    },
    {
      label: "Details",
      value: outcome.details || outcome.reason || "N/A",
      fullWidth: true,
    },
  ];

  return (
    <div
      key="survey-outcome"
      style={{
        gridColumn: "1 / -1",
        width: "100%",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 700,
          letterSpacing: "0.04em",
          marginBottom: "6px",
        }}
      >
        Survey Outcome
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px 24px",
        }}
      >
        {surveyFields.map((field) => (
          <div
            key={field.label}
            style={{
              minWidth: 0,
              gridColumn: field.fullWidth ? "1 / -1" : undefined,
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                marginBottom: "4px",
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              {field.label}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.85)",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {field.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BaseActivityColumns(args: {
  toggleViewInteractionSection: (currentActivityId: string) => void;
  toggleViewRequestsSection: (currentActivityId: string) => void;
  toggleViewRedemptionRequestsSection: (currentActivityId: string) => void;
  toggleInteractionEdit: () => void;
  toggleCreateTicket: () => void;
  showRequests?: boolean;
  isEditing?: boolean;
  isTicketFormOpen?: boolean;
  actionLabelMode?: "default" | "closeOnOpen";
  transfersRequests: TransferRequest[];
  forCompany?: boolean;
}): ColumnType<Activity>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "ID",
      render(text: string, record: Activity): JSX.Element {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Interaction Date",
      dataIndex: "activityDate",
      key: "Activity Date",
      render(text: string, record: Activity): JSX.Element {
        return (
          <span>
            {record.activityDate
              ? moment(record.activityDate, "YYYY-MM-DD HH:mm:ss").format(
                  "llll",
                )
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "Description",
    },
    {
      title: "Sub Category",
      dataIndex: ["subCategory", "name"],
      key: "Sub Category",
    },
    {
      title: "State",
      dataIndex: "status",
      key: "state",
      render(text: string, record: Activity): JSX.Element {
        return (
          <Tag
            color={getStateColor(record.status)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(record.status)}
          </Tag>
        );
      },
    },
    {
      title: "Closed By",
      dataIndex: "closedBy",
      key: "closedBy",
      render(text: string, record: Activity): JSX.Element {
        return <p>{record.closedBy ? record.closedBy : "N/A"}</p>;
      },
    },
    {
      title: "Date Closed",
      dataIndex: "closedAt",
      key: "closedAt",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record.closedAt
              ? moment(record.closedAt, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </p>
        );
      },
    },
    {
      title: "Closure Type",
      dataIndex: "closureType",
      key: "closureType",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p style={{ textTransform: "capitalize" }}>
            {record.closureType ? record.closureType : "N/A"}
          </p>
        );
      },
    },
    {
      title: "Interaction Category",
      dataIndex: "interactionCategory",
      key: "interactionCategory",
      render(text: string, record: Activity): JSX.Element {
        return <p>{formatInteractionCategory(record.interactionCategory)}</p>;
      },
    },
    {
      title: "Source Contact Information",
      dataIndex: "sourceContactInformation",
      key: "sourceContactInformation",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record?.sourceContactInformation
              ? record?.sourceContactInformation
              : "N/A"}
          </p>
        );
      },
    },
    ...(args.forCompany
      ? [
          {
            title: "Contact Person",
            dataIndex: ["customer", "fullname"],
            key: "contactPerson",
            render(text: string, record: Activity): JSX.Element {
              return <p>{record?.customer?.fullname || "N/A"}</p>;
            },
          },
        ]
      : []),
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      render(text: string, record: Activity): JSX.Element {
        return <p>{record?.comment ? record.comment : "No comments yet"}</p>;
      },
    },
    {
      title: "Revert Date",
      dataIndex: "revertDate",
      key: "Revert Date",
      render(text: string, record: Activity): JSX.Element {
        const date = record?.revertDate ? record?.revertDate : moment();
        return (
          <p>
            {date ? moment(date, "YYYY-MM-DDTHH:mm:ssZ").format("LL") : "N/A"}
          </p>
        );
      },
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "Due Date",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record.dueDate
              ? moment(record.dueDate, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </p>
        );
      },
    },
    {
      title: "Requested By",
      dataIndex: "user",
      key: "user",
      render(text: string, record: Activity): JSX.Element {
        return <p>{record?.user?.name ? record.user.name : "N/A"}</p>;
      },
    },
    {
      title: "Contact Information",
      dataIndex: "contactInformation",
      key: "contactInformation",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record?.contactInformation
              ? record.contactInformation
              : "Not provided"}
          </p>
        );
      },
    },
    {
      title: "Preferred Comm Mode",
      dataIndex: "preferredCommMode",
      key: "Preferred Comm Mode",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record?.preferredCommMode
              ? record.preferredCommMode
              : "No preference"}
          </p>
        );
      },
    },
    {
      title: "Desired Outcome",
      dataIndex: "desiredOutcome",
      key: "Desired Outcome",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record?.desiredOutcome ? record.desiredOutcome : "Not specified"}
          </p>
        );
      },
    },
    {
      title: "Complaint Type",
      dataIndex: "complaintType",
      key: "Complaint Type",
      render(text: string, record: Activity): JSX.Element {
        return (
          <p>
            {record?.complaintType === "not_applicable" ||
            !record?.complaintType
              ? "N/A"
              : record?.complaintType}
          </p>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "edit",
      key: "edit",
      render(text: string, record: Activity): JSX.Element {
        const requests = args.transfersRequests ? args.transfersRequests : [];
        const transferRequest = requests.find(
          (transfer: TransferRequest): boolean => {
            return transfer.customerInteraction?.id.includes(record.id);
          },
        );

        const transferCondition =
          record.subCategory?.longName?.includes("Transfer In") ||
          record.subCategory?.longName?.includes("Transfer Out") ||
          record.subCategory?.longName?.includes("Merge Accounts");

        const redemptionCondition =
          record.subCategory?.longName?.includes("Redemption") ||
          record.subCategory?.longName?.includes("redemption");

        if (transferCondition && args.showRequests) {
          if (transferRequest) {
            return (
              <div>
                <EntitledSection
                  entitlement={entitlements.Customers.interactions.edit}
                >
                  <NavLink
                    id="edit-btn"
                    to="#"
                    onClick={(
                      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                    ): void => {
                      e.preventDefault();
                      args.toggleInteractionEdit();
                    }}
                    style={{
                      marginRight: "10px",
                    }}
                  >
                    Edit
                  </NavLink>
                </EntitledSection>
                <NavLink
                  id="view-requests-btn"
                  to={`/transfer-requests/${transferRequest.id}`}
                >
                  View Request
                </NavLink>
              </div>
            );
          }

          return (
            <div>
              <EntitledSection
                entitlement={entitlements.Customers.interactions.edit}
              >
                <NavLink
                  id="edit-btn"
                  to="#"
                  onClick={(
                    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                  ): void => {
                    e.preventDefault();
                    args.toggleInteractionEdit();
                  }}
                  style={{
                    marginRight: "10px",
                  }}
                >
                  Edit
                </NavLink>
              </EntitledSection>

              <NavLink
                id="create-requests-btn"
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                ): void => {
                  e.preventDefault();
                  args.toggleViewRequestsSection(record.id);
                }}
                to={`/transfer-requests/${record.id}`}
              >
                Create Request
              </NavLink>
            </div>
          );
        }

        if (redemptionCondition && args.showRequests) {
          return (
            <div>
              <EntitledSection
                entitlement={entitlements.Customers.interactions.edit}
              >
                <NavLink
                  id="edit-btn"
                  to="#"
                  onClick={(
                    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                  ): void => {
                    e.preventDefault();
                    args.toggleInteractionEdit();
                  }}
                  style={{
                    marginRight: "10px",
                  }}
                >
                  Edit
                </NavLink>
              </EntitledSection>

              <NavLink
                id="view-redemptions-btn"
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                ): void => {
                  e.preventDefault();
                  args.toggleViewRedemptionRequestsSection(record.id);
                }}
                to={`/redemption-requests/${record.id}`}
              >
                Create Request
              </NavLink>
            </div>
          );
        }

        return (
          <div>
            <EntitledSection
              entitlement={entitlements.Customers.interactions.edit}
            >
              <NavLink
                id="edit-btn"
                to="#"
                style={{
                  marginRight: "10px",
                }}
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                ): void => {
                  e.preventDefault();
                  args.toggleInteractionEdit();
                }}
              >
                {args.actionLabelMode === "closeOnOpen" && args.isEditing
                  ? "Cancel Edit"
                  : "Edit"}
              </NavLink>
            </EntitledSection>
            <NavLink
              id="ticket-btn"
              to="#"
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
              ): void => {
                e.preventDefault();
                args.toggleCreateTicket();
              }}
              style={{
                marginRight: "10px",
              }}
            >
              {args.actionLabelMode === "closeOnOpen" && args.isTicketFormOpen
                ? "Cancel Ticket"
                : "Create Ticket"}
            </NavLink>
          </div>
        );
      },
    },
  ];
}

export function ColumnCreator(
  toggleViewInteractionSection: (currentActivityId: string) => void,
): ColumnType<Activity>[] {
  return [
    {
      title: "Interaction Date",
      dataIndex: "activityDate",
      key: "Activity Date",
      render(text: string, record: Activity): JSX.Element {
        return (
          <span>
            {record.activityDate
              ? moment(record.activityDate, "YYYY-MM-DD HH:mm:ss").format(
                  "llll",
                )
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "Description",
      width: "30%",
    },
    {
      title: "Sub Category",
      dataIndex: ["subCategory", "name"],
      key: "Sub Category",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render(text: string, record: Activity): JSX.Element {
        return <p>{convertStatusToReadableString[record.status]}</p>;
      },
    },

    {
      title: "Interaction Source",
      dataIndex: ["interactionSource", "name"],
      key: "Interaction Source",
      render(text: string, record: Activity): JSX.Element {
        return <p>{record.interactionSource.name}</p>;
      },
    },
    {
      title: "Interaction Category",
      dataIndex: "interactionCategory",
      key: "interactionCategory",
      render(text: string, record: Activity): JSX.Element {
        return <p>{formatInteractionCategory(record.interactionCategory)}</p>;
      },
    },
    {
      title: "View",
      dataIndex: "id",
      key: "activityId",
      align: "center" as const,
      render(text: string, record: Activity): JSX.Element {
        return (
          <div>
            <NavLink
              id="view-interaction-btn"
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
              ): void => {
                e.preventDefault();
                toggleViewInteractionSection(record.id);
              }}
              to={`/notification-records/${record.id}`}
            >
              <EyeOutlined style={{}} />
            </NavLink>
          </div>
        );
      },
    },
  ];
}

interface EditingViewProps {
  reference: RefObject<FormInstance>;
  updateInteraction: () => Promise<void>;
  interaction: Activity;
  style?: React.CSSProperties;
  isComplaint?: boolean;
  showHeading?: boolean;
  company?: Company;
}

export function EditingView(props: EditingViewProps): JSX.Element {
  const showHeading =
    typeof props.showHeading === "undefined" ? true : props.showHeading;
  const companyContacts = props.company?.companyContacts || [];
  const contactPersons = generateFirstSelectOption(
    "Select Contact Person",
  ).concat(
    companyContacts.map((contactPerson) => {
      return {
        id: contactPerson.id,
        name: contactPerson.contactName,
      };
    }) as SelectOptionsInterface[],
  );
  const selectedCustomerId = props.interaction.customer?.id;
  const selectedContactPersonId = selectedCustomerId
    ? companyContacts.find(
        (contactPerson) => contactPerson.customer?.id === selectedCustomerId,
      )?.id
    : undefined;
  const activityFields: FormFieldConfig[] = [
    {
      id: "description",
      name: "description",
      required: true,
      inputType: "textArea",
      rule: "string",
      label: "Description",
      initialValue: props.interaction.description,
      placeholder: "Enter description",
      colConfig: {
        span: 24,
      },
    },
    {
      id: "comment",
      name: "comment",
      required: true,
      inputType: "string",
      rule: "string",
      label: "Comment",
      initialValue: props.interaction.comment,
      placeholder: "Enter Comment",
      colConfig: {
        span: 7,
      },
    },
    {
      id: "contactInformation",
      name: "contactInformation",
      required: false,
      inputType: "string",
      rule: "string",
      label: "Contact Information",
      initialValue: props.interaction.contactInformation,
      placeholder: "Enter Contact Information",
      colConfig: {
        span: 7,
        offset: 1,
      },
    },
    {
      id: "revertDate",
      name: "revertDate",
      inputType: "defaultDate",
      rule: "object",
      required: false,
      label: "Revert Date",
      elementStyle: {
        width: "100%",
      },
      initialValue: props.interaction.revertDate
        ? moment(props.interaction.revertDate)
        : moment(),
      colConfig: {
        span: 7,
        offset: 1,
      },
    },
    {
      id: "complaintType",
      name: "complaintType",
      rule: "string",
      inputType: "select",
      required: false,
      label: "Complaint Type",
      initialValue: props.interaction.complaintType,
      placeholder: "Complaint Type",
      selectOptions: generateComplaintTypes(),
      colConfig: {
        span: 7,
      },
    },
    ...(props.company
      ? [
          {
            id: "customerId",
            name: "customerId",
            placeholder: "Contact Person",
            required: false,
            inputType: "select",
            rule: "string",
            label: "Contact Person",
            initialValue: selectedContactPersonId,
            selectOptions: contactPersons,
            elementStyle: {
              width: "100%",
            },
            colConfig: {
              span: 7,
              offset: 1,
            },
          } as FormFieldConfig,
        ]
      : []),
    {
      name: "createInteractionBtn",
      value: "Update Interaction",
      inputType: "button",
      required: false,
      elementStyle: {
        borderRadius: 0,
        height: "33px",
        marginTop: "12px",
      },
      className: "base-button",
      handleClick: props.updateInteraction,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <div
      style={{
        ...props.style,
      }}
    >
      {showHeading && (
        <h1
          style={{
            marginBottom: "20px",
            color: "rgba(0, 0, 0, 0.85)",
            fontWeight: "bold",
            fontSize: "16px",
            lineHeight: "1.5",
          }}
        >
          Edit Interaction
        </h1>
      )}
      <Row>
        <Col span={24}>
          <EntitledSection
            entitlement={entitlements.BusinessDevelopment.interactions.edit}
          >
            <div
              className="view-opportunity-row create-interaction-row"
              style={{
                marginTop: 0,
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fields={activityFields}
                fieldsToHide={[]}
              ></BaseForm>
            </div>
          </EntitledSection>
        </Col>
      </Row>
    </div>
  );
}

export default class ActivitiesSection extends React.Component<Props, State> {
  attachmentsReference: RefObject<FormInstance>;
  constructor(props: Props) {
    super(props);
    this.state = {
      preferredCommMode: "",
      commModeFieldsToHide: ["phone", "email"],
      ticketFieldToHide: [
        "ticketSubCategoryId",
        "ticketSubCategoryDueDate",
        "ticketDescription",
        "assigneeId",
      ],
      interactionFieldsToHide: [],
      subCategories: [],
      allSubCategories: [],
      interactionSources: [],
      complaintFieldsToHide: [
        "desiredOutcome",
        "preferredCommMode",
        "complaintType",
      ],
      isLoading: false,
      productsWithSubscriptionId: [],
      selectedProduct: [],
      companiesWithCustomerCompanyAssociationId: [],
      selectedCompany: [],
      products: [],
      companies: [],
      subscribers: [],
      mentions: [],
      users: [],
      userQuery: "",
      subCategoryQuery: "",
      allSubCategoryQuery: "",
      isComplaint: false,
      ticketSubCategoryDueDate: "",
      sourceContactType: "string",
    };
    this.attachmentsReference = React.createRef<FormInstance>();
  }

  setRef = (
    name: "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  renderDetailsColumns = (
    activities: Activity[],
    columns: ColumnType<Activity>[],
  ): JSX.Element => {
    const activity = activities[0];
    if (!activity) {
      return (
        <div style={{ padding: "16px 0", color: "rgba(0, 0, 0, 0.45)" }}>
          No interaction details available.
        </div>
      );
    }

    const getValue = (
      record: Activity,
      dataIndex?: string | number | (string | number)[],
    ): any => {
      if (!dataIndex) return undefined;
      if (Array.isArray(dataIndex)) {
        return dataIndex.reduce((acc: any, key: string | number) => {
          if (acc === null || acc === undefined) return undefined;
          return acc[key as keyof typeof acc];
        }, record as any);
      }
      return (record as any)[dataIndex];
    };

    let descriptionItem: JSX.Element | null = null;
    let commentItem: JSX.Element | null = null;
    const surveyOutcomeItem = renderSurveyOutcome(
      normalizeInteractionClosureMetadata(activity.closureMetadata)?.survey,
    );
    const detailItems = columns.reduce<JSX.Element[]>((acc, column, index) => {
      const label =
        typeof column.title === "string" || typeof column.title === "number"
          ? String(column.title)
          : "";
      if (label.toLowerCase() === "action") return acc;
      const rawValue = getValue(activity, column.dataIndex as any);
      const value = column.render
        ? column.render(rawValue, activity, 0)
        : rawValue ?? "N/A";
      const isDescription = label.toLowerCase() === "description";
      const isComment = label.toLowerCase() === "comment";
      const hasDescriptionData =
        isDescription &&
        typeof rawValue === "string" &&
        rawValue.trim().length > 0;
      const isFullRow = isComment || hasDescriptionData;

      const item = (
        <div
          key={`${label}-${index}`}
          style={{
            minWidth: 0,
            gridColumn: isFullRow ? "1 / -1" : undefined,
            alignSelf: isFullRow ? "start" : undefined,
            justifySelf: isFullRow ? "start" : undefined,
            width: isFullRow ? "100%" : undefined,
            maxWidth: isFullRow ? "92%" : undefined,
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              marginBottom: "6px",
            }}
          >
            {label}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "rgba(0, 0, 0, 0.85)",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {value}
          </div>
        </div>
      );

      if (isDescription) {
        descriptionItem = item;
      } else if (isComment) {
        commentItem = item;
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    if (descriptionItem) {
      detailItems.push(
        <div
          key="description-divider"
          style={{
            gridColumn: "1 / -1",
            width: "100%",
          }}
        >
          <Divider className="view-opportunity-ant-divider" />
        </div>,
      );
      detailItems.push(descriptionItem);
    }
    if (commentItem) detailItems.push(commentItem);
    if (surveyOutcomeItem) {
      detailItems.push(
        <div
          key="survey-outcome-divider"
          style={{
            gridColumn: "1 / -1",
            width: "100%",
          }}
        >
          <Divider className="view-opportunity-ant-divider" />
        </div>,
      );
      detailItems.push(surveyOutcomeItem);
    }

    return (
      <div className="interaction-details-card">
        <div className="interaction-details-grid">{detailItems}</div>
      </div>
    );
  };

  renderDetailRequestAction = (activity?: Activity): JSX.Element | null => {
    if (!activity || !this.props.showRequests) return null;
    const requests = this.props.transferRequests || [];
    const transferRequest = requests.find(
      (transfer: TransferRequest): boolean =>
        transfer.customerInteraction?.id.includes(activity.id),
    );

    const transferCondition =
      activity.subCategory?.longName?.includes("Transfer In") ||
      activity.subCategory?.longName?.includes("Transfer Out") ||
      activity.subCategory?.longName?.includes("Merge Accounts");

    const redemptionCondition =
      activity.subCategory?.longName?.includes("Redemption") ||
      activity.subCategory?.longName?.includes("redemption");

    if (transferCondition) {
      return transferRequest ? (
        <NavLink to={`/transfer-requests/${transferRequest.id}`}>
          View Request
        </NavLink>
      ) : (
        <NavLink
          onClick={(
            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
          ): void => {
            e.preventDefault();
            this.props.toggleViewRequestsSection(activity.id);
          }}
          to={`/transfer-requests/${activity.id}`}
        >
          Create Request
        </NavLink>
      );
    }

    if (redemptionCondition) {
      return (
        <NavLink
          onClick={(
            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
          ): void => {
            e.preventDefault();
            this.props.toggleViewRedemptionRequestsSection(activity.id);
          }}
          to={`/redemption-requests/${activity.id}`}
        >
          Create Request
        </NavLink>
      );
    }

    return null;
  };

  getInteraction = (): void => {
    this.props.createInteraction(
      this.state.selectedCompany,
      this.state.selectedProduct,
    );
  };

  getSubCategories = (): void => {
    const { subCategoryQuery } = this.state;
    request({
      query: GET_SUB_CATEGORIES,
      variables: {
        page: 1,
        q: subCategoryQuery,
        departmentId: this.props.forCustomer ? 5 : 4,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          subCategories: {
            results: SubCategory[];
          };
        };
      }>): void => {
        this.setState({
          subCategories: data.data.subCategories.results,
        });
      },
    );
  };

  getAllSubCategories = (): void => {
    const { allSubCategoryQuery } = this.state;
    request({
      query: GET_SUB_CATEGORIES,
      variables: {
        page: 1,
        q: allSubCategoryQuery,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          subCategories: {
            results: SubCategory[];
          };
        };
      }>): void => {
        this.setState({
          allSubCategories: data.data.subCategories.results,
        });
      },
    );
  };

  getSubscriberList = (): void => {
    request({
      query: GET_SUBSCRIBERS,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          subscribers: [];
        };
      }>): void => {
        this.setState({
          subscribers: data.data.subscribers,
        });
      },
    );
  };

  getInteractionSources = (): void => {
    request({
      query: GET_INTERACTION_SOURCES,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          interactionSources: SelectOptionsInterface[];
        };
      }>): void => {
        const productsWithSubscriptionId: ProductWithSubscriptionId[] = [];
        const companiesWithCustomerCompanyAssociationId: CompanyWithCustomerCompanyAssociationId[] =
          [];
        const products: Product[] = [];
        const companies: Company[] = [];
        this.props.customer?.customerSubscriptions?.map(
          (subs: Subscription) => {
            productsWithSubscriptionId.push({
              ...subs.product,
              subscriptionId: subs.id,
            });
            products.push({ ...subs.product });
          },
        );

        this.props.company?.companySubscriptions?.map((subs: Subscription) => {
          productsWithSubscriptionId.push({
            ...subs.product,
            subscriptionId: subs.id,
          });
          products.push({ ...subs.product });
        });

        if (
          this.props.customer?.employers &&
          this.props.customer?.employers.length > 0
        ) {
          this.props.customer?.employers?.map((cca: Company) => {
            companiesWithCustomerCompanyAssociationId.push({
              ...cca,
              customerCompanyAssociationId: cca.id,
            });
            companies.push({ ...cca });
          });
        }

        if (
          this.props.customer?.formerCompanies &&
          this.props.customer?.formerCompanies.length > 0
        ) {
          this.props.customer?.formerCompanies?.map((cca: Company) => {
            companiesWithCustomerCompanyAssociationId.push({
              ...cca,
              customerCompanyAssociationId: cca.id,
            });
            companies.push({ ...cca });
          });
        }

        this.setState({
          products,
          productsWithSubscriptionId,
          companies,
          companiesWithCustomerCompanyAssociationId,
          interactionSources: data.data.interactionSources,
        });
      },
    );
  };

  handleInteractionSourceSelect = (selectedId: string): void => {
    const selectedSource = this.state.interactionSources.find(
      (source) => source.id === selectedId,
    );

    if (!selectedSource) return;

    if (selectedSource.name.includes("Email")) {
      this.setState({
        sourceContactType: "email",
      });
    } else if (
      selectedSource.name.includes("Phone") ||
      selectedSource.name.includes("Telephone")
    ) {
      this.setState({
        sourceContactType: "phone",
      });
    } else {
      this.setState({
        sourceContactType: "string",
      });
    }
  };

  loadSelectBoxValues = (): void => {
    this.getInteractionSources();
    this.getSubCategories();
    this.getAllSubCategories();
    this.toggleActivityType();
    this.getSubscriberList();
    this.getAllUsers();
  };

  toggleActivityType = () => {
    if (!this.props.company) {
      this.setState({ interactionFieldsToHide: ["customerId"] });
    } else {
      this.setState({
        interactionFieldsToHide: ["customerCompanyAssociation"],
      });
    }
  };

  handleContactTypeChange = (preferredCommMode: string): void => {
    let commModeFieldsToHide: string[] = [];

    switch (preferredCommMode) {
      case "phone":
        commModeFieldsToHide = ["email"];
        break;
      case "email":
        commModeFieldsToHide = ["phone"];
        break;
      default:
        commModeFieldsToHide = [];
        break;
    }

    this.setState({
      preferredCommMode,
      commModeFieldsToHide,
    });
  };

  handleProductSelect = (products: string): void => {
    const product = this.state.productsWithSubscriptionId.filter((product) =>
      products.includes(product.id),
    );
    this.setState({ selectedProduct: product });
  };

  handleEmployerSelect = (employers: string): void => {
    const employer =
      this.state.companiesWithCustomerCompanyAssociationId.filter((company) =>
        employers.includes(company.id),
      );
    this.setState({ selectedCompany: employer });
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: { url, name },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      const newAttachment = mutation.attachment;

      this.props.updateAttachments(newAttachment);

      if (
        this.props.assignAttachmentsToCompanyInteraction &&
        this.props.currentInteractionId
      ) {
        this.props.assignAttachmentsToCompanyInteraction(
          [newAttachment.id],
          this.props.currentInteractionId,
        );
      } else if (
        this.props.assignAttachmentsToCustomerInteraction &&
        this.props.currentInteractionId
      ) {
        this.props.assignAttachmentsToCustomerInteraction(
          [newAttachment.id],
          this.props.currentInteractionId,
        );
      }

      this.attachmentsReference.current?.resetFields();
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  getInteractionCreateForm = (
    activityFields: FormFieldConfig[],
    isLoading: boolean,
  ): JSX.Element => {
    const fieldsToHide = [
      ...this.state.interactionFieldsToHide,
      ...this.state.complaintFieldsToHide,
      ...this.state.commModeFieldsToHide,
      ...this.state.ticketFieldToHide,
    ];

    return (
      <Spin spinning={isLoading}>
        <BaseForm
          layout="vertical"
          reference={this.props.reference}
          fields={applyTwoColumnOffsets(activityFields, fieldsToHide)}
          fieldsToHide={fieldsToHide}
        >
          <div>
            <AttachmentsSection
              reference={this.attachmentsReference}
              createAttachment={this.createAttachment}
              attachments={this.props.attachments}
              alignVertical
              showForm={true}
              handleRemove={this.props.removeAttachment}
            />
          </div>
        </BaseForm>
      </Spin>
    );
  };

  getAllUsers = (): void => {
    const { userQuery } = this.state;
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: userQuery,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  };

  triggerDelayedSearch = debounce(async (forUser = false): Promise<void> => {
    forUser ? await this.getAllUsers() : await this.getSubCategories();
  }, DEBOUNCE_TIME);

  triggerDelayedSearchAllSubCategories = debounce(async (): Promise<void> => {
    await this.getAllSubCategories();
  }, DEBOUNCE_TIME);

  handleUserSearchInput = async (q: string): Promise<void> => {
    this.setState({ userQuery: q }, () => {
      this.triggerDelayedSearch(true);
    });
  };

  handleSubCategorySearchInput = async (q: string): Promise<void> => {
    this.setState({ subCategoryQuery: q }, () => {
      this.triggerDelayedSearch();
    });
  };

  handleTicketSubCategorySearchInput = async (q: string): Promise<void> => {
    this.setState({ allSubCategoryQuery: q }, () => {
      this.triggerDelayedSearchAllSubCategories();
    });
  };

  handleSubCategorySelect = (subCategoryId: string): void => {
    const subCategory = this.state.subCategories.find(
      (sub) => sub.id === subCategoryId,
    );

    const isComplaint = subCategory?.longName
      ?.toLowerCase()
      .includes("complaint");

    let complaintFieldsToHide: string[] = [];
    const commModeFieldsToHide: string[] = ["phone", "email"];

    if (isComplaint) {
      complaintFieldsToHide = [];
    } else {
      complaintFieldsToHide = [
        "preferredCommMode",
        "desiredOutcome",
        "complaintType",
      ];
    }

    this.setState({
      complaintFieldsToHide,
      commModeFieldsToHide,
      isComplaint: isComplaint as boolean,
    });
  };

  handleTicketStatusSelect = (status: string): void => {
    let ticketFieldToHide: string[] = [];
    switch (status) {
      case "yes":
        ticketFieldToHide = [];
        break;
      case "no":
        ticketFieldToHide = [
          "ticketSubCategoryId",
          "ticketSubCategoryDueDate",
          "ticketDescription",
          "assigneeId",
        ];
        break;
      default:
        ticketFieldToHide = [
          "ticketSubCategoryId",
          "ticketSubCategoryDueDate",
          "ticketDescription",
          "assigneeId",
        ];
        break;
    }

    this.setState({
      ticketFieldToHide,
    });
  };

  activityDateChangeHandler = async (): Promise<void> => {
    const subCategoryId = this.props.reference.current?.getFieldValue(
      "ticketSubCategoryId",
    );
    if (subCategoryId === undefined) return;
    await this.ticketSubCategorySelect("nothing");
  };

  ticketSubCategorySelect = async (id: string): Promise<void> => {
    const activityDate =
      this.props.reference.current?.getFieldValue("activityDate");

    const subCategoryId = this.props.reference.current?.getFieldValue(
      "ticketSubCategoryId",
    );

    if (activityDate === undefined) return;

    await request({
      query: CALCULATE_DUE_DATE_QUERY,
      variables: {
        subCategoryId,
        activityDate,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        ticketSubCategoryDueDate: data.calculateSubCategoryDueDate,
      });
    });
  };

  render(): JSX.Element {
    const {
      subCategories,
      allSubCategories,
      interactionSources,
      products,
      companies,
      subscribers,
      users,
      isComplaint,
      ticketSubCategoryDueDate,
      sourceContactType,
    } = this.state;

    const { activities, useBaseColumn } = this.props;

    const modifiedProducts: Product[] = injectKey<Product>(products, "name");

    const modifiedCompanies = companies.map((company) => {
      return { ...company, name: `${company.name}-${company.petraId}` };
    });

    const modifiedEmployers: Company[] = injectKey<Company>(
      modifiedCompanies,
      "name",
    );

    const modifiedSubscribers: Subscriber[] = subscribers.map((sub) => {
      return { ...sub, id: sub.id, name: sub.user.name };
    });

    const modifiedSubCategories: SelectOptionsInterface[] = subCategories.map(
      (sub: SubCategory) => {
        return { ...sub, id: sub?.id, name: sub?.longName || sub?.name };
      },
    );

    const allModifiedSubCategories: SelectOptionsInterface[] =
      allSubCategories.map((sub: SubCategory) => {
        return { ...sub, id: sub?.id, name: sub?.longName || sub?.name };
      });

    const modifiedUsers = generateFirstSelectOption("Select Assignee").concat(
      users.map((user) => {
        return { ...user, id: user.id, name: user.name };
      }),
    );
    const contactPersons = generateFirstSelectOption(
      "Select Contact Person",
    ).concat(
      this.props.company?.companyContacts?.map((contactPerson) => {
        return {
          id: contactPerson.id,
          name: contactPerson.contactName,
        };
      }) as SelectOptionsInterface[],
    );

    const status: SelectOptionsInterface[] = [
      { id: "yes", name: "Yes" },
      { id: "no", name: "No" },
    ];

    const activityFields: FormFieldConfig[] = [
      {
        id: "interactionSourceId",
        name: "interactionSourceId",
        placeholder: "Interaction Source",
        required: true,
        inputType: "select",
        rule: "string",
        selectHandler: this.handleInteractionSourceSelect,
        label: "Interaction Source",
        selectOptions: interactionSources,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "subCategoryId",
        name: "subCategoryId",
        placeholder: "Sub Category",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Sub Category",
        isSearching: true,
        selectOptions: modifiedSubCategories,
        changeHandler: this.handleSubCategorySearchInput,
        selectHandler: this.handleSubCategorySelect,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "sourceContactInformation",
        name: "sourceContactInformation",
        required: true,
        inputType: sourceContactType,
        rule: sourceContactType === "email" ? "email" : "string",
        label: "Source Contact Information",
        placeholder: "Enter Contact Info",
        colConfig: {
          span: 11,
        },
        rules: [
          {
            async validator(_, value) {
              const parsedPhone = parsePhoneNumberFromString("+" + value!);
              if (parsedPhone) {
                if (parsedPhone.isValid() || parsedPhone.isPossible()) {
                  return;
                }
                throw new Error("Invalid Phone Number");
              }
            },
          },
        ],
      },
      {
        id: "customerSubscription",
        name: "customerSubscription",
        placeholder: "Products",
        required: false,
        inputType: "multipleSelect",
        rule: "string",
        label: "Products",
        changeHandler: this.handleProductSelect,
        selectHandler: this.handleProductSelect,
        isArrayField: true,
        selectOptions: modifiedProducts,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "customerCompanyAssociation",
        name: "customerCompanyAssociation",
        placeholder: "Employers",
        required: true,
        inputType: "multipleSelect",
        rule: "string",
        label: "Employers",
        changeHandler: this.handleEmployerSelect,
        selectHandler: this.handleEmployerSelect,
        isArrayField: true,
        selectOptions: modifiedEmployers,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "activityDate",
        name: "activityDate",
        inputType: "defaultDate",
        rule: "object",
        required: true,
        label: "Activity Date",
        dateChangeHandler: this.activityDateChangeHandler,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },

      {
        id: "desiredOutcome",
        name: "desiredOutcome",
        required: true,
        inputType: "string",
        rule: "string",
        label: "Desired Outcome",
        placeholder: "Enter desired outcome",
        colConfig: {
          span: 11,
        },
      },
      {
        id: "complaintType",
        name: "complaintType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Complaint Type",
        placeholder: "Complaint Type",
        selectOptions: generateComplaintTypes(),
        colConfig: {
          span: 11,
        },
      },

      {
        id: "preferredCommMode",
        name: "preferredCommMode",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Preferred Comm Mode",
        placeholder: "Preferred Comm Mode",
        changeHandler: this.handleContactTypeChange,
        selectOptions: generateContactTypes(),
        colConfig: {
          span: 11,
        },
      },
      {
        id: "email",
        name: "email",
        inputType: "string",
        rule: "email",
        label: "Contact Information",
        placeholder: "Enter email address ...",
        required: true,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "phone",
        name: "phone",
        inputType: "phone",
        rule: "string",
        label: "Contact Information",
        placeholder: "Enter phone number",
        required: true,
        elementStyle: { width: "100%" },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "createTicket",
        name: "createTicket",
        placeholder: "Should Interaction create ticket?",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Should Interaction create ticket?",
        selectOptions: status,
        changeHandler: this.handleTicketStatusSelect,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "ticketSubCategoryId",
        name: "ticketSubCategoryId",
        placeholder: "Ticket SubCategory",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Ticket Sub Category",
        isSearching: true,
        selectOptions: allModifiedSubCategories,
        changeHandler: this.handleTicketSubCategorySearchInput,
        selectHandler: this.ticketSubCategorySelect,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "ticketSubCategoryDueDate",
        name: "ticketSubCategoryDueDate",
        placeholder: "Select a sub-category",
        required: true,
        inputType: "description",
        disabled: true,
        rule: "string",
        label: "Ticket Sub Category Due Date",
        value: ticketSubCategoryDueDate,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "customerId",
        name: "customerId",
        placeholder: "Contact Person",
        required: false,
        inputType: "select",
        rule: "string",
        label: "Contact Person",
        selectOptions: contactPersons,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "sourceName",
        name: "sourceName",
        placeholder: "Company Reference",
        required: false,
        inputType: "string",
        rule: "string",
        label: "Company Reference",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "mentions",
        name: "mentions",
        placeholder: "Mentions",
        required: false,
        inputType: "multipleSelect",
        rule: "string",
        label: "Mentions",
        isArrayField: true,
        selectOptions: modifiedSubscribers,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "interactionCategory",
        name: "interactionCategory",
        placeholder: "Interaction Category",
        required: true,
        inputType: "select",
        rule: "string",
        label: "Interaction Category",
        selectOptions: interactionCategoryOptions,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "assigneeId",
        name: "assigneeId",
        placeholder: "Assignee",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Assignee",
        isSearching: true,
        selectOptions: modifiedUsers,
        changeHandler: this.handleUserSearchInput,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "description",
        name: "description",
        required: true,
        inputType: "textArea",
        rule: "string",
        label: "Interaction Description",
        placeholder: "Enter description",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "ticketDescription",
        name: "ticketDescription",
        required: true,
        inputType: "textArea",
        rule: "string",
        label: "Ticket Description",
        placeholder: "Enter ticket description",
        autoSize: { minRows: 6, maxRows: 6 },
        colConfig: {
          span: 24,
        },
      },
    ];

    let columns: ColumnType<Activity>[] = [];
    if (useBaseColumn) {
      columns = BaseActivityColumns({
        toggleViewInteractionSection: this.props.toggleViewInteractionSection,
        toggleViewRequestsSection: this.props.toggleViewRequestsSection,
        toggleViewRedemptionRequestsSection:
          this.props.toggleViewRedemptionRequestsSection,
        toggleInteractionEdit: this.props.toggleInteractionEdit,
        transfersRequests: this.props.transferRequests,
        toggleCreateTicket: this.props.toggleCreateTicket,
        showRequests: this.props.showRequests,
        isEditing: this.props.isEditing,
        isTicketFormOpen: this.props.isTicketFormOpen,
        actionLabelMode: this.props.actionLabelMode,
        forCompany: this.props.forCompany,
      });
    } else {
      columns = ColumnCreator(this.props.toggleViewInteractionSection);
    }

    const detailRequestAction = this.renderDetailRequestAction(activities[0]);

    return (
      <Spin spinning={this.props.isInteractionLoading}>
        <Fragment>
          <div
            style={
              this.props.showBorder
                ? {
                    border: "1px solid rgb(232 232 232)",
                    padding: "20px",
                    boxSizing: "border-box",
                    ...this.props.style,
                  }
                : this.props.style
            }
          >
            {this.props.showHeading && (
              <Row>
                <Col span={12}>
                  <h1
                    style={{
                      marginBottom: "20px",
                      color: "rgba(0, 0, 0, 0.85)",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "1.5",
                    }}
                  >
                    Interactions
                  </h1>
                </Col>
                <Col
                  span={12}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    textAlign: "right",
                    marginBottom: "25px",
                  }}
                >
                  <div style={{ display: "flex", columnGap: 8 }}>
                    {this.props.showForm && !this.props.isReadOnly && (
                      <EntitledSection
                        entitlement={
                          entitlements.BusinessDevelopment.interactions.create
                        }
                      >
                        <Button
                          style={{
                            fontWeight: "normal",
                            height: "40px",
                            marginRight: "3px",
                          }}
                          className="base-button"
                          onClick={() => {
                            this.loadSelectBoxValues();
                            this.props.toggleCreateInteractionModal();
                          }}
                          ref={this.props.createInteractionButtonRef}
                        >
                          Create Interaction
                        </Button>
                        <ExportData
                          model={
                            this.props.company
                              ? "company_interaction"
                              : "customer_interaction"
                          }
                          dataArray={activities}
                        />
                      </EntitledSection>
                    )}
                    {this.props.showForm && this.props.isReadOnly && (
                      <ExportData
                        model={
                          this.props.company
                            ? "company_interaction"
                            : "customer_interaction"
                        }
                        dataArray={activities}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            )}

            {this.props.showForm && (
              <Collapse
                onChange={this.props.handlePanelChange}
                style={{ marginBottom: "18px" }}
              >
                <Collapse.Panel key="interactions" header="Interactions">
                  <EntitledSection
                    entitlement={
                      entitlements.BusinessDevelopment.interactions.list
                    }
                  >
                    <Table
                      scroll={{ x: 500 }}
                      bordered={true}
                      columns={columns}
                      dataSource={injectKey(activities, "id")}
                      pagination={{
                        defaultPageSize: 25,
                        total: this.props.activitiesCount,
                        onChange: this.props.nextPage,
                      }}
                    />
                  </EntitledSection>
                </Collapse.Panel>
              </Collapse>
            )}

            {!this.props.showForm && (
              <Row>
                <Col>
                  <EntitledSection
                    entitlement={
                      entitlements.BusinessDevelopment.interactions.list
                    }
                  >
                    {(this.props.detailLayout ?? "table") === "columns" ? (
                      <Fragment>
                        {this.renderDetailsColumns(activities, columns)}
                        {this.props.showDetailActions &&
                          useBaseColumn &&
                          detailRequestAction && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "14px",
                              }}
                            >
                              {detailRequestAction}
                            </div>
                          )}
                      </Fragment>
                    ) : (
                      <Table
                        scroll={{ x: 500 }}
                        bordered={true}
                        columns={columns}
                        dataSource={injectKey(activities, "id")}
                        pagination={{
                          defaultPageSize: 25,
                          total: this.props.activitiesCount,
                          onChange: this.props.nextPage,
                        }}
                      />
                    )}
                  </EntitledSection>
                </Col>
              </Row>
            )}

            {this.props.isEditing &&
              this.props.updateInteraction &&
              ModalCreator({
                title: "Edit Interaction",
                okType: "primary",
                okText: "Close",
                width: 900,
                showModal: this.props.isEditing,
                onOkay: this.props.toggleInteractionEdit,
                toggleModal: this.props.toggleInteractionEdit,
                showFooter: false,
                inscription: (
                  <EditingView
                    interaction={activities[0] as Activity}
                    updateInteraction={this.props.updateInteraction}
                    reference={this.props.reference}
                    isComplaint={isComplaint}
                    showHeading={false}
                    company={
                      this.props.forCompany ? this.props.company : undefined
                    }
                  />
                ),
              })}
            {this.props.showInteraction &&
              ModalCreator({
                title: "Create Interaction",
                okType: "primary",
                okText: "Create",
                width: 1100,
                isLoading: this.state.isLoading,
                showModal: this.props.showInteraction,
                onOkay: this.getInteraction,
                toggleModal: this.props.toggleCreateInteractionModal,
                showFooter: true,
                maskClosable: false,
                okayButtonProps: {
                  disabled: this.props.disableCreateInteractionButton ?? false,
                },
                inscription: this.getInteractionCreateForm(
                  activityFields,
                  this.props.isInteractionLoading,
                ),
              })}
          </div>
        </Fragment>
      </Spin>
    );
  }
}
